import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    isEqual as _isEqual,
    cloneDeep as _cloneDeep
} from 'lodash';

import { makeStyles } from '@mui/styles';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { DataGridPro } from '@mui/x-data-grid-pro';

import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import ConfirmationDialog from '../../../../../components/confirmationdlg';
import Validator, { useNoisyValidate, checkValidity } from '../../../../../components/validator';

import SystemService from '../../../../../data/system-service';

import Helpers from '../../../../../libraries/helpers';
import Auth from '../../../../../data/auth-service';
import DataContext from '../../../../../data/data-context';

const useStyles = makeStyles(theme => ({
    userButtons: {
        float: 'right',
        marginTop: theme.spacing(4),
        marginRight: theme.spacing(1),
        marginLeft: theme.spacing(1),
    },
    clearfix: {
        overflow: 'auto',
    },
    buttonRight: {
        float: 'right',
        marginLeft: theme.spacing(2),
    },
    newUserForm: {
        width: '100%',
        marginLeft: 0,
        marginRight: 0,
        '& .MuiFormControlLabel-label': {
            width: '100%',
        },
        '& .MuiInputBase-root': {
            width: '100%',
        }
    },
    header: {
        borderBottom: `1px solid rgba(${theme.palette.mode === 'dark' ? '81, 81, 81' : '224, 224, 224'}, 1)`,
    },
}));

const NEW_USER_DATA = {
    username: '',
    seclevel: 'authPriv',
    authtype: 'SHA',
    authpass: '',
    privtype: 'AES',
    privpass: '',
};

const SNMP = () => {
    const classes = useStyles();
    const navigate = useNavigate();
    const [snmpTable, setSNMPTable] = useState(false);
    const [newUser, setNewUser] = useState(false);
    const [newUserData, setNewUserData] = useState(_cloneDeep(NEW_USER_DATA));
    const [editUser, setEditUser] = useState('');
    const [deleteUserDialog, setDeleteUserDialog] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const { systemSettings, updateSystemSettings } = useContext(DataContext);
    const [snmp, setSNMP] = useState({
        syscontact: '',
        syslocation: '',
        communities: [],
        users: [],
    });
    const [original, setOriginal] = useState({});
    const [changes, setChanges] = useState(false);
    const snmpForm = useNoisyValidate({});
    const snmpPerms = Auth.permissions.get('SNMP');

    useEffect(() => {
        if (systemSettings) {
            let s = _cloneDeep(systemSettings.snmp);
            if (!s) {
                s = {
                    syscontact: '',
                    syslocation: '',
                    communities: [],
                    users: [],
                };
            } else if (s && !s.users) {
                s.users = [];
            }
            s.users = s.users.map((v) => { return { ...v, id: Helpers.UniqueID() } });
            setOriginal(_cloneDeep(s));
            setSNMP(s);
            setSNMPTable(true);
        }
    }, [systemSettings]);

    useEffect(() => {
        setChanges(_isEqual(snmp, original));
        setSNMPTable(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [snmp]);

    const handleLocation = (e) => {
        setSNMP({
            ...snmp,
            syslocation: e.currentTarget.value,
        });
    };

    const handleContact = (e) => {
        setSNMP({
            ...snmp,
            syscontact: e.currentTarget.value,
        });
    };

    const handleCommunityStrings = (e) => {
        setSNMP({
            ...snmp,
            communities: e.currentTarget.value ? e.currentTarget.value.replace(/\s/g, '').split(',') : null,
        });
    };

    const handleSelectUser = (s) => {
        setSelectedUser(s.row);
    };

    const handleEditUser = (e) => {
        setTimeout(() => {
            setNewUserData(selectedUser);
            setNewUser(true);
            setEditUser(selectedUser.username);
        });
    };

    const handleOpenDeleteUser = (e) => {
        setDeleteUserDialog(true);
    };

    const handleCloseDeleteUser = () => {
        setDeleteUserDialog(false);
    };

    const handleDeleteUser = () => {
        setSNMPTable(false);
        const s = { ...snmp };
        const i = s.users.findIndex((v, i) => { return v.username === selectedUser.username });
        s.users.splice(i, 1);
        setSNMP(s);
        setDeleteUserDialog(false);
    };

    const handleUsername = (e, validID, valid, useEffect) => {
        snmpForm.set(current => ({ ...current, [validID]: valid }));
        setNewUserData({
            ...newUserData,
            username: e.target.value,
        });
    };

    const handleSeclevel = (e) => {
        setNewUserData({
            ...newUserData,
            seclevel: e.target.value,
        });
    };

    const handleAuthType = (e) => {
        setNewUserData({
            ...newUserData,
            authtype: e.target.value,
        });
    };

    const handleAuthPass = (e, validID, valid) => {
        snmpForm.set({
            ...snmpForm.get(),
            [validID]: editUser && e.target.value === '***' ? true : valid
        });

        setNewUserData({
            ...newUserData,
            authpass: e.target.value,
        });
    };

    const handlePrivType = (e) => {
        setNewUserData({
            ...newUserData,
            privtype: e.target.value,
        });
    };

    const handlePrivPass = (e, validID, valid) => {
        snmpForm.set({
            ...snmpForm.get(),
            [validID]: editUser && e.target.value === '***' ? true : valid
        });

        setNewUserData({
            ...newUserData,
            privpass: e.target.value,
        });
    };

    const handleAddNewUser = () => {
        setSNMPTable(false);
        const s = _cloneDeep(snmp);

        if (editUser) {
            const i = s.users.findIndex((v, i) => { return v.username === editUser });
            s.users[i] = {
                ...newUserData,
                id: Helpers.UniqueID(),
            };
        } else {
            s.users.push({
                ...newUserData,
                id: Helpers.UniqueID(),
            });
        }
        setSNMP(s);
        handleCancelNewUser();
    };

    const handleCancelNewUser = () => {
        setEditUser('');
        setNewUserData(_cloneDeep(NEW_USER_DATA));
        setNewUser(false);
    };

    const handleSave = () => {
        updateSystemSettings({ snmp });
    };

    const handleCancel = () => {
        navigate('/system/configuration');
    };

    const handleDownloadMIB = (file) => {
        SystemService.downloadMIB(file + '.txt').then(res => {
            const a = document.createElement('a');
            a.target = '_blank';
            a.download = `${file}.txt`;
            a.href = URL.createObjectURL(new Blob([res], { type: 'application/octet-stream' }), `${file}.txt`);
            a.click();
        });
    };

    const handleRemoveValidation = (id) => {
        const s = snmpForm.get();
        delete s[id];
        snmpForm.set(s);
    };

    return (
        <Paper elevation={3}>
            <Box p={5}>
                <Grid container spacing={2}>
                    <Grid item xs={5}>
                        <Box>
                            <TextField
                                disabled={!snmpPerms.write}
                                aria-label="system location"
                                label="System Location"
                                fullWidth
                                value={snmp.syslocation}
                                onChange={handleLocation}
                            />
                        </Box>
                        <Box mt={3}>
                            <TextField
                                disabled={!snmpPerms.write}
                                aria-label="system contact"
                                label="System Contact"
                                fullWidth
                                value={snmp.syscontact}
                                onChange={handleContact}
                            />
                        </Box>
                        <Box mt={3}>
                            <TextField
                                disabled={!snmpPerms.write}
                                aria-label="read only community string"
                                label="Read-Only Community String"
                                helperText="Comma separated community strings"
                                fullWidth
                                value={snmp.communities || ''}
                                onChange={handleCommunityStrings}
                            />
                        </Box>
                        <Box mt={3}>
                            <Box display="inline-block" mr={2}>
                                <Button aria-label="Download BRICATA-MIB" download onClick={() => { handleDownloadMIB('BRICATA-MIB') }}>BRICATA-MIB</Button>
                            </Box>
                            <Box display="inline-block">
                                <Button aria-label="Download BRICATA-CMC-MIB" download onClick={() => { handleDownloadMIB('BRICATA-CMC-MIB') }}>BRICATA-CMC-MIB</Button>
                            </Box>
                            <Box display="inline-block">
                                <Button aria-label="Download BRICATA-SENSOR-MIB" download onClick={() => { handleDownloadMIB('BRICATA-SENSOR-MIB') }}>BRICATA-SENSOR-MIB</Button>
                            </Box>
                            <Box display="inline-block">
                                <Button aria-label="Download BRICATA-IF-MIB" download onClick={() => { handleDownloadMIB('BRICATA-IF-MIB') }}>BRICATA-IF-MIB</Button>
                            </Box>
                            <Box display="inline-block">
                                <Button aria-label="Download BRICATA-TRAP-MIB" download onClick={() => { handleDownloadMIB('BRICATA-TRAP-MIB') }}>BRICATA-TRAP-MIB</Button>
                            </Box>
                            <Box display="inline-block">
                                <Button aria-label="Download BRICATA-DATANODE-MIB" download onClick={() => { handleDownloadMIB('BRICATA-DATANODE-MIB') }}>BRICATA-DATANODE-MIB</Button>
                            </Box>
                        </Box>
                    </Grid>
                    <Grid item xs={7}>
                        <Box className={classes.fullWidth} height={300}>
                            {snmpTable && <DataGridPro
                                components={{
                                    Header: !snmpPerms.write ? undefined : () => {
                                        return (
                                            <Box display="flex" width="100%" justifyContent="flex-end" className={classes.header}>
                                                <IconButton
                                                    variant="contained"
                                                    color="primary"
                                                    aria-label="edit user"
                                                    onClick={handleEditUser}
                                                    disabled={!selectedUser}
                                                    size="large">
                                                    <EditIcon />
                                                </IconButton>
                                                <IconButton
                                                    variant="contained"
                                                    color="secondary"
                                                    aria-label="delete user"
                                                    onClick={handleOpenDeleteUser}
                                                    disabled={!selectedUser}
                                                    size="large">
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Box>
                                        );
                                    },
                                }}
                                rows={snmp.users}
                                columns={[
                                    { sortable: true, flex: 1, type: 'string', disableColumnMenu: true, hidden: false, headerName: 'Username', field: 'username', renderCell: d => { return <div>{d.row.username}</div> } },
                                    { sortable: true, flex: 1, type: 'string', disableColumnMenu: true, hidden: false, headerName: 'Security Level', field: 'seclevel', renderCell: d => { return <div>{d.row.seclevel}</div> } },
                                    { sortable: true, flex: 1, type: 'string', disableColumnMenu: true, hidden: false, headerName: 'Auth Protocol', field: 'authtype', renderCell: d => { return <div>{d.row.authtype}</div> } },
                                    { sortable: true, flex: 1, type: 'string', disableColumnMenu: true, hidden: false, headerName: 'Privacy Protocol', field: 'privtype', renderCell: d => { return <div>{d.row.privtype}</div> } },
                                ]}
                                disableMultipleSelection
                                onRowClick={handleSelectUser}
                            />}
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        <Box my={3} display={(newUser || !snmpPerms.write) ? 'none' : 'block'}>
                            <Button className={classes.buttonRight} variant="contained" color="primary" aria-label="add read-only user" onClick={() => { setNewUser(!newUser) }}>Read-Only User +</Button>
                        </Box>
                        <Paper elevation={3}>
                            <Box mt={3} p={5} display={newUser ? 'block' : 'none'}>
                                <Grid container item xs={12} spacing={5}>
                                    <Grid item xs={6}>
                                        <Box>
                                            {newUser && <Validator
                                                valueType="snmpName"
                                                errorText="SNMP username is invalid"
                                                aria-label="username"
                                                label="Username"
                                                fullWidth
                                                value={newUserData.username}
                                                onChange={handleUsername}
                                                onRemoval={handleRemoveValidation}
                                            />}
                                        </Box>
                                        <Box mt={3}>
                                            <FormControl fullWidth>
                                                <InputLabel id="snmp-auth-protocol-label">Auth Protocol</InputLabel>
                                                <Select
                                                    aria-label="auth protocol"
                                                    labelId="snmp-auth-protocol-label"
                                                    label="Auth Protocol"
                                                    value={newUserData.authtype}
                                                    onChange={handleAuthType}
                                                >
                                                    <MenuItem value="SHA">SHA</MenuItem>
                                                    <MenuItem value="MD5">MD5</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Box>
                                        <Box mt={3}>
                                            <FormControl fullWidth>
                                                <InputLabel id="snmp-privacy-protocol-label">Privacy Protocol</InputLabel>
                                                <Select
                                                    aria-label="privacy protocol"
                                                    labelId="snmp-privacy-protocol-label"
                                                    label="Privacy Protocol"
                                                    value={newUserData.privtype}
                                                    onChange={handlePrivType}
                                                >
                                                    <MenuItem value="AES">AES</MenuItem>
                                                    <MenuItem value="DES">DES</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Box>
                                            <FormControl fullWidth>
                                                <InputLabel id="snmp-security-level-label">Security Level</InputLabel>
                                                <Select
                                                    aria-label="security level"
                                                    labelId="snmp-security-level-label"
                                                    label="Security Level"
                                                    value={newUserData.seclevel}
                                                    onChange={handleSeclevel}
                                                >
                                                    <MenuItem value="authPriv">authPriv</MenuItem>
                                                    <MenuItem value="authNoPriv">authNoPriv</MenuItem>
                                                    <MenuItem value="noAuthNoPriv">noAuthNoPriv</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Box>
                                        <Box mt={3}>
                                            {newUser && <Validator
                                                valueType="length"
                                                errorText="Must be a minimum of 8 characters"
                                                inputProps={{ min: 8 }}
                                                label="Auth Password Phrase"
                                                aria-label="auth password phrase"
                                                type="password"
                                                fullWidth
                                                value={newUserData.authpass}
                                                onChange={handleAuthPass}
                                                onRemoval={handleRemoveValidation}
                                            />}
                                        </Box>
                                        <Box mt={3}>
                                            {newUser && <Validator
                                                valueType="length"
                                                errorText="Must be a minimum of 8 characters"
                                                inputProps={{ min: 8 }}
                                                label="Privacy Password Phrase"
                                                aria-label="privacy password phrase"
                                                type="password"
                                                fullWidth
                                                value={newUserData.privpass}
                                                onChange={handlePrivPass}
                                                onRemoval={handleRemoveValidation}
                                            />}
                                        </Box>
                                    </Grid>
                                </Grid>
                                <div className={classes.clearfix}>
                                    <Button
                                        className={classes.userButtons}
                                        variant="contained"
                                        color="primary"
                                        aria-label="add"
                                        onClick={handleAddNewUser}
                                        disabled={!checkValidity(snmpForm.get())}
                                    >
                                        {editUser ? 'Update' : 'Add'}
                                    </Button>
                                    <Button className={classes.userButtons} variant="contained" color="secondary" aria-label="cancel" onClick={handleCancelNewUser}>Cancel</Button>
                                </div>
                            </Box>
                        </Paper>
                    </Grid>
                    {snmpPerms.write && <Grid item xs={12}>
                        <Button
                            className={classes.buttonRight}
                            variant="contained"
                            color="primary"
                            aria-label="save"
                            onClick={handleSave}
                            disabled={changes}
                        >
                            Save
                        </Button>
                        <Button className={classes.buttonRight} variant="contained" color="secondary" aria-label="cancel" onClick={handleCancel}>Cancel</Button>
                    </Grid>}
                </Grid>
                <ConfirmationDialog
                    onClose={handleCloseDeleteUser}
                    onOK={handleDeleteUser}
                    open={deleteUserDialog}
                    message={`Are you sure you want to remove the user ${selectedUser && selectedUser.username}?`}
                    title="Delete User"
                    cancelLabel="Cancel"
                    okLabel="Yes"
                />
            </Box>
        </Paper>
    );
};

export default SNMP;
